import { useEffect, useRef, useState } from 'react';
import type { LookAtThatChatMessage } from '../../lib/lookAtThatApi';

const SUGGESTIONS = [
  'What is the game RTP?',
  'What is the max win?',
  'What are the min and max bets?',
  'How does the bonus feature trigger?',
  'Is there an autoplay limit?',
];

interface Props {
  messages: LookAtThatChatMessage[];
  pending: boolean;
  running: boolean;
  executing: boolean;
  onSend: (message: string) => void;
}

/**
 * Chatbox on the live session: ask a question, watch the bot navigate the
 * game in the viewer, get a grounded answer back (quoting on-screen values).
 */
export function LookAtThatChat({ messages, pending, running, executing, onSend }: Props) {
  const [draft, setDraft] = useState('');
  const feedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending]);

  const canSend = running && !executing && draft.trim().length > 0;
  const send = () => {
    if (!canSend) return;
    onSend(draft.trim());
    setDraft('');
  };

  return (
    <div className="card look-at-that-chat">
      <div className="row" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Ask the bot</h3>
        <span className="spacer" />
        <span className="muted small">
          {!running ? 'Start a session to chat' : pending ? 'Bot is working on it…' : 'Answers come from the live game'}
        </span>
      </div>

      <div ref={feedRef} className="look-at-that-chat-feed">
        {messages.length === 0 && (
          <p className="muted small" style={{ margin: 0 }}>
            Ask anything about the game — the bot opens menus / paytable as needed and answers with
            what it actually sees. Try one of the suggestions below.
          </p>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`look-at-that-chat-msg ${m.role}`}>
            <div className="look-at-that-chat-bubble">
              {m.content}
              <span className="muted small look-at-that-chat-ts">
                {new Date(m.ts).toLocaleTimeString()}
              </span>
            </div>
          </div>
        ))}
        {pending && (
          <div className="look-at-that-chat-msg assistant">
            <div className="look-at-that-chat-bubble muted">
              <span className="look-at-that-typing-dot" style={{ display: 'inline-block', marginRight: 6 }} />
              navigating the game to answer…
            </div>
          </div>
        )}
      </div>

      <div className="row" style={{ flexWrap: 'wrap', gap: 6, margin: '8px 0' }}>
        {SUGGESTIONS.map((sg) => (
          <button
            key={sg}
            type="button"
            className="btn small btn-ghost"
            disabled={!running || executing}
            onClick={() => onSend(sg)}
          >
            {sg}
          </button>
        ))}
      </div>

      <div className="row" style={{ gap: 8 }}>
        <input
          style={{ flex: 1 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={running ? 'e.g. What is the game RTP?' : 'Start a session first'}
          disabled={!running || executing}
        />
        <button type="button" className="btn btn-primary" disabled={!canSend} onClick={send}>
          {pending || executing ? 'Working…' : 'Ask'}
        </button>
      </div>
    </div>
  );
}
